import { Button, NineSliceContainer } from "@ravalmatic/catafractum-utils";
import { Call, DOUBLE, PASS, REDOUBLE } from "@ravalmatic/bridge-engine/core";
import { GameVars } from "../../../GameVars";
import { BoardPhase } from "../../../Types";
import { BiddingManager, BiddingSnapshot } from "../BiddingManager";
import { BoardManager, BoardPhaseSnapshot } from "../BoardManager";
import { BiddingGrid } from "./BiddingGrid";
import { ContractContainer } from "./ContractContainer";

export class BiddingMenu extends Phaser.GameObjects.Container {

    private static readonly WIDTH = 450;
    private static readonly HEIGHT = 490;
    private static readonly BORDER_SIZE = 60;
    private static readonly PASS_BUTTON_Y = 225;
    private static readonly BUTTON_SPACING = 210;

    private readonly bg: NineSliceContainer;
    private readonly biddingGrid: BiddingGrid;
    private readonly passBtn: Button;
    private readonly doubleBtn: Button;
    private readonly redoubleBtn: Button;
    private readonly contractContainer: ContractContainer;
    private readonly unsubscribe: () => void;
    private readonly unsubscribeFromPhase: () => void;
    private readonly biddingManager: BiddingManager;
    private animationScale = .85;

    constructor(scene: Phaser.Scene, private readonly boardManager: BoardManager) {

        super(scene);

        this.relayout();
        this.biddingManager = boardManager.getBiddingManager();

        this.bg = new NineSliceContainer(this.scene, 0, 0, "texture_atlas_1", "bg_generic", BiddingMenu.WIDTH, BiddingMenu.HEIGHT, BiddingMenu.BORDER_SIZE);
        this.add(this.bg);

        this.biddingGrid = new BiddingGrid(scene, this.onCallSelected.bind(this));
        this.add(this.biddingGrid);

        this.passBtn = new Button(scene, -210, BiddingMenu.PASS_BUTTON_Y, "texture_atlas_1", "btn_pass");
        this.passBtn.setInflationTween(1.05);
        this.passBtn.onUp(() => this.onCallSelected(PASS), this);

        this.doubleBtn = new Button(scene, 0, BiddingMenu.PASS_BUTTON_Y, "texture_atlas_1", "btn_double");
        this.doubleBtn.setInflationTween(1.05);
        this.doubleBtn.onUp(() => this.onCallSelected(DOUBLE), this);

        this.redoubleBtn = new Button(scene, -this.passBtn.x, BiddingMenu.PASS_BUTTON_Y, "texture_atlas_1", "btn_redouble");
        this.redoubleBtn.setInflationTween(1.05);
        this.redoubleBtn.onUp(() => this.onCallSelected(REDOUBLE), this);

        this.add([this.passBtn, this.doubleBtn, this.redoubleBtn]);
        this.contractContainer = new ContractContainer(
            scene, this.onContinue.bind(this), BiddingMenu.PASS_BUTTON_Y
        );
        this.add(this.contractContainer);
        this.unsubscribe = this.biddingManager.subscribe(this.refresh.bind(this));
        this.unsubscribeFromPhase = boardManager.subscribeToPhase(this.onPhaseChanged.bind(this));

        this.alpha = 0;
        this.scene.tweens.add({
            targets: this,
            alpha: 1,
            animationScale: 1,
            onUpdate: () => this.relayout(),
            duration: 450,
            ease: Phaser.Math.Easing.Cubic.Out
        });
    }

    public destroy(fromScene?: boolean): void {

        if (!this.scene) {
            return;
        }

        this.unsubscribe();
        this.unsubscribeFromPhase();
        this.scene.tweens.killTweensOf(this);
        super.destroy(fromScene);
    }

    private onPhaseChanged(snapshot: BoardPhaseSnapshot): void {

        if (snapshot.phase !== BoardPhase.CONTRACT) {
            return;
        }

        this.biddingGrid.destroy();
        [this.passBtn, this.doubleBtn, this.redoubleBtn].forEach(button => button.destroy());

        if (snapshot.presentation === null) {
            throw new Error("Contract phase requires a contract presentation");
        }
        this.contractContainer.showContract(snapshot.presentation);
    }

    private onContinue(): void {

        this.boardManager.requestContinue();
    }

    public setPlayerAvatarId(avatarId: number): void {

        this.contractContainer.setPlayerAvatarId(avatarId);
    }

    public relayout(): void {

        this.setPosition(GameVars.gameWidth / 2, GameVars.gameHeight / 2 - 95 * GameVars.scaleY);
        this.setScale(this.animationScale, this.animationScale * GameVars.scaleY);
    }

    public hideAnimated(onComplete: () => void): void {

        this.contractContainer.disableContinue();
        this.scene.tweens.killTweensOf(this);
        this.scene.tweens.add({
            targets: this,
            alpha: 0,
            animationScale: this.animationScale * .85,
            onUpdate: () => this.relayout(),
            duration: 500,
            ease: "Cubic.In",
            onComplete: () => {
                this.destroy();
                onComplete();
            }
        });
    }

    private onCallSelected(call: Call): void {

        if (this.boardManager.getPhaseSnapshot().phase === BoardPhase.BIDDING) {
            this.biddingManager.submitPlayerCall(call);
        }
    }

    private refresh(snapshot: BiddingSnapshot): void {

        if (this.boardManager.getPhaseSnapshot().phase !== BoardPhase.BIDDING) {
            return;
        }

        this.biddingGrid.refresh(snapshot.legalCalls, snapshot.selectedCall, snapshot.isPlayerTurn);
        this.setButtonAvailable(this.passBtn, snapshot.isPlayerTurn && snapshot.legalCalls.includes(PASS));
        this.setButtonAvailable(this.doubleBtn, snapshot.isPlayerTurn && snapshot.legalCalls.includes(DOUBLE));
        this.setButtonAvailable(this.redoubleBtn, snapshot.isPlayerTurn && snapshot.legalCalls.includes(REDOUBLE));
        this.positionButtons();
    }

    private setButtonAvailable(button: Button, available: boolean): void {

        button.setVisible(available);
        if (available) {
            button.enable();
        } else {
            button.disable();
        }
    }

    private positionButtons(): void {

        const visibleButtons = [this.passBtn, this.doubleBtn, this.redoubleBtn]
            .filter(button => button.visible);
        const firstX = -((visibleButtons.length - 1) * BiddingMenu.BUTTON_SPACING) / 2;

        visibleButtons.forEach((button, index) => {
            button.setPosition(firstX + index * BiddingMenu.BUTTON_SPACING, BiddingMenu.PASS_BUTTON_Y);
        });
    }
}
